import type { OperationsTab } from '@ops/shared/model';
import type { FindOperationsInput } from '@travel-planner/shared/schemas/operations';
import { UTCDate } from '@date-fns/utc';
import { OPERATIONS_TAB } from '@ops/shared/model';
import { timeWindowDates } from './time-window';

// Resolved at render time so cards whose filter depends on "now" / org settings
// stay a single source of truth in the model instead of leaking into the page.
export type CardFilterContext = {
  now: UTCDate;
  /** Org operation-status ids that mean "awaiting confirmation" (dashboard-v2). */
  awaitingStatusIds?: string[];
  /**
   * Org-customisable "default" guide languages to exclude (XT-380 Languages).
   * Falls back to EN/ES/PT when empty (`settings.opsDashboard.guideLanguageExclude`).
   */
  guideLanguageExclude?: string[];
};

export type OperationsFilterCard = {
  id: string;
  label: string;
  /** Secondary line under the label (design: aging hint or descriptor). */
  subText: string;
  /** Amber aging-hint styling for `subText` (design cards 1 & 2). */
  agingHint?: boolean;
  /** Accent dot colour (design data-viz accents). */
  dot: string;
  /**
   * Info-tooltip copy: what the number counts and what filter clicking the
   * card applies to the list below.
   */
  description: string;
  /**
   * Preset overlaid onto the list query when this card is selected. A function
   * so it can read `now` / resolved status ids / org settings.
   */
  filter: (ctx: CardFilterContext) => Partial<FindOperationsInput>;
};

// ─── Shared status-code constants (org-agnostic codes, resolved server-side) ─
const CODE_MODIFIED = 'S0' as const;
const CODE_NON_REQUESTED = 'S1' as const;
const CODE_DELIVERED = 'S7' as const;
const CODE_CANCELLED_LIKE = ['S90', 'S91'] as const; // cancelled / rejected terminal

// Languages treated as the default set for the Guides "Languages" card.
export const DEFAULT_GUIDE_LANGUAGES = ['en', 'es', 'pt'] as const;

// Itinerary status labels for the Ready Home card (case-insensitive match
// server-side). Ready ≈ Reservation Suppliers + Reservations OK.
//
// The Cancelled card deliberately has NO name list — XT-505 re-keyed it onto
// `itinerary.cancelled_on` so it no longer depends on how an org names the
// status. Don't reintroduce one here.
const READY_ITINERARY_STATUSES = [
  'RESERVAS FORNECEDORES/OPTITRAVEL',
  'RESERVAS OK',
  'Reservation Suppliers',
  'Reservations OK',
] as const;

/**
 * Rolling window for the Home "Cancelled itineraries" card (XT-506): the
 * counter shows itineraries moved to Cancelled in the last 7 days, inclusive
 * of today. 7 days = today + the 6 preceding days.
 */
export const CANCELLED_WINDOW_DAYS = 7;

/**
 * Rolling window for the Home "Itineraries ready" card: itineraries that moved
 * to Reservation Suppliers / Reservations OK in the last 15 days, inclusive of
 * today. 15 days = today + the 14 preceding days (same convention as
 * `CANCELLED_WINDOW_DAYS`).
 *
 * The card MUST own a backward window. A status transition is a PAST event
 * (`operation_log.created_on`), but with no window of its own the card
 * inherited the page range, which on the Active view is `today … today + 7`.
 * A forward window can never contain a past transition, so the counter read 0
 * for every org regardless of how many files had just gone ready.
 */
export const READY_WINDOW_DAYS = 15;

const addDaysIso = (
  now: UTCDate,
  daysFrom: number,
  daysTo: number
): { startDate: string; endDate: string } => {
  const start = new UTCDate(now);
  start.setUTCHours(0, 0, 0, 0);
  start.setUTCDate(start.getUTCDate() + daysFrom);
  const end = new UTCDate(now);
  end.setUTCHours(23, 59, 59, 999);
  end.setUTCDate(end.getUTCDate() + daysTo);
  return { startDate: start.toISOString(), endDate: end.toISOString() };
};

// Category scope per tab (mirrors operations-layout sub-tab filter).
export const tabContentFilter = (
  tab: OperationsTab
): Partial<FindOperationsInput> => {
  switch (tab) {
    case OPERATIONS_TAB.Guides:
      return { contentSubcategory: ['Guide'] };
    case OPERATIONS_TAB.Tickets:
      return { contentSubcategory: ['Ticket'] };
    case OPERATIONS_TAB.Transport:
      return { contentCategory: ['Transportation'] };
    default:
      return {};
  }
};

// A card that supplies its own date window (or skips the window entirely)
// replaces the page window outright — otherwise the page window applies. The
// merge lives in ONE place so the card count and the drilled-in list can never
// drift (the exact-count invariant depends on it). Used by both the card count
// (filter-cards.tsx) and the list input (operations-layout / dashboard).
export const overlayCardFilter = (
  base: FindOperationsInput,
  cardFilter: Partial<FindOperationsInput>
): FindOperationsInput => ({
  ...base,
  ...cardFilter,
  // Card-owned date windows replace the page window.
  ...(cardFilter.startDate ||
  cardFilter.endDate ||
  cardFilter.skipDateRangeFilter
    ? {
        startDate: cardFilter.startDate ?? base.startDate,
        endDate: cardFilter.endDate ?? base.endDate,
      }
    : {}),
});

// ─── Ops Dashboard v2 cards (XT-225) ────────────────────────────────────────
// Fixed six-card quick views for `/operations/dashboard-v2`. Distinct from the
// XT-380 per-tab cards on the normal `/operations` list.

export const DASHBOARD_V2_CARDS: OperationsFilterCard[] = [
  {
    id: 'unassigned',
    label: 'Unassigned',
    subText: 'drivers & guides',
    agingHint: true,
    dot: '#fb923c',
    description:
      'Services with no driver or guide assigned yet. Click to filter the list; use the toolbar window for Today / Next 3 days / This week.',
    filter: () => ({ tourOperatorAssigned: 'unassigned' }),
  },
  {
    id: 'failed-messages',
    label: 'Failed messages',
    subText: 'message failures',
    agingHint: true,
    dot: '#e11d48',
    description:
      'Items with at least one failed WhatsApp send. Click to filter the list to those items.',
    filter: () => ({ failedMessages: true }),
  },
  {
    id: 'flagged',
    label: 'Flagged',
    subText: 'needs review',
    dot: '#ef4444',
    description:
      'Itineraries with a red flag — a note on a service or a live incident. Click to filter the list to flagged items.',
    filter: () => ({ flagged: 'flagged' }),
  },
  {
    id: 'awaiting',
    label: 'Awaiting',
    subText: 'next 3 days',
    dot: '#f97316',
    description:
      'Services awaiting confirmation from the supplier/operator. Click to filter the list to awaiting items.',
    filter: (ctx) => ({
      operationStatuses: ctx.awaitingStatusIds ?? [],
    }),
  },
  {
    id: 'arriving-today',
    label: 'Arriving today',
    subText: 'check-ins',
    dot: '#2dd4bf',
    description:
      "Services with a check-in today, regardless of the selected time window. Click to filter the list to today's arrivals.",
    filter: (ctx) => timeWindowDates('today', ctx.now),
  },
  {
    id: 'all',
    label: 'All open',
    subText: 'everything',
    dot: '#a1a1aa',
    description:
      'Every open service in the current time window, unfiltered. Click to clear the card filter.',
    filter: () => ({}),
  },
];

// `all` is the reset / base list (no overlay) on dashboard-v2.
export const DEFAULT_FILTER_CARD_ID = 'all';

export const getDashboardFilterCard = (
  id?: string
): OperationsFilterCard | undefined =>
  id ? DASHBOARD_V2_CARDS.find((card) => card.id === id) : undefined;

// ─── Per-tab card sets on /operations (XT-380) ──────────────────────────────

const homeCards: OperationsFilterCard[] = [
  {
    id: 'home-modified',
    label: 'Modified',
    subText: 'op. status',
    agingHint: true,
    dot: '#f472b6',
    description:
      'Items with operation status Modified (S0). Click to list them with itinerary ref, location, owner, and status.',
    filter: () => ({ operationStatusCodes: [CODE_MODIFIED] }),
  },
  {
    id: 'home-last-minute',
    label: 'Last Minute',
    subText: 'starts < 1 week',
    agingHint: true,
    dot: '#fb923c',
    description:
      'Items starting within 7 days that are still Non-Requested (S1).',
    filter: (ctx) => ({
      ...addDaysIso(ctx.now, 0, 6),
      operationStatusCodes: [CODE_NON_REQUESTED],
    }),
  },
  {
    id: 'home-starting',
    label: 'Itineraries starting',
    subText: 'in window',
    dot: '#2dd4bf',
    description:
      'Number of itineraries whose start date falls in the chosen time window. Click to list their items.',
    filter: () => ({
      itineraryDateMode: 'start',
      countMode: 'itineraries',
    }),
  },
  {
    id: 'home-ongoing',
    label: 'Itineraries ongoing',
    subText: 'in window',
    dot: '#60a5fa',
    description:
      'Number of itineraries that overlap the chosen time window. Click to list their items.',
    filter: () => ({
      itineraryDateMode: 'ongoing',
      countMode: 'itineraries',
    }),
  },
  {
    id: 'home-ready',
    label: 'Itineraries ready',
    subText: 'moved in last 15 days',
    dot: '#34d399',
    description:
      'Number of itineraries that moved to Reservation Suppliers or Reservations OK in the last 15 days. Click to list their items.',
    filter: (ctx) => ({
      // The counter is scoped to the last 15 days, not the page window: the
      // window bounds the `operation_log` transition timestamp, which is always
      // in the past. The card owns it, so `overlayCardFilter` replaces the page
      // range — see READY_WINDOW_DAYS.
      ...addDaysIso(ctx.now, -(READY_WINDOW_DAYS - 1), 0),
      itineraryStatuses: [...READY_ITINERARY_STATUSES],
      itineraryStatusTransitionedTo: [...READY_ITINERARY_STATUSES],
      countMode: 'itineraries',
    }),
  },
  {
    id: 'home-cancelled',
    label: 'Cancelled itineraries',
    subText: 'moved in last 7 days',
    dot: '#94a3b8',
    description:
      'Number of itineraries cancelled in the last 7 days while in an operational status (e.g. Reservations OK, Ongoing). Click to list their items.',
    filter: (ctx) => ({
      // XT-506: the counter is scoped to the last 7 days of the cancellation,
      // not the page window and not all history. The card owns its window, so
      // `overlayCardFilter` replaces the page range — see CANCELLED_WINDOW_DAYS.
      ...addDaysIso(ctx.now, -(CANCELLED_WINDOW_DAYS - 1), 0),
      // XT-505: keyed on `itinerary.cancelled_on`, NOT on an operation-log
      // transition to a status *named* "Cancelled". `cancelled_on` is stamped on
      // entry to sales_stage 20, so orgs that renamed the status (e.g. CANCELADO)
      // are counted too, and no log row needs to exist. `itineraryStatuses` is
      // likewise dropped: it matched the same literal names, so keeping it would
      // re-introduce exactly the org-dependence this change removes.
      // The server narrows this to files cancelled while on an operational
      // status (`cancelledWhileOperationalCondition`); `includeNonOperational`
      // only lifts the whitelist on the CURRENT (cancelled) status.
      itineraryCancelledOnWithin: true,
      includeNonOperational: true,
      countMode: 'itineraries',
    }),
  },
];

const guidesCards: OperationsFilterCard[] = [
  {
    id: 'guides-modified',
    label: 'Modified',
    subText: 'guide items',
    agingHint: true,
    dot: '#f472b6',
    description: 'Guide subitems with operation status Modified (S0).',
    filter: () => ({
      ...tabContentFilter(OPERATIONS_TAB.Guides),
      operationStatusCodes: [CODE_MODIFIED],
    }),
  },
  {
    id: 'guides-starting-soon',
    label: 'Starting soon',
    subText: '< 15 days',
    agingHint: true,
    dot: '#fb923c',
    description:
      'Guide subitems starting within 15 days that are still Non-Requested (S1).',
    filter: (ctx) => ({
      ...tabContentFilter(OPERATIONS_TAB.Guides),
      ...addDaysIso(ctx.now, 0, 14),
      operationStatusCodes: [CODE_NON_REQUESTED],
    }),
  },
  {
    id: 'guides-languages',
    label: 'Languages',
    subText: 'not EN / ES / PT',
    dot: '#a78bfa',
    description:
      'Guide subitems whose language is not English, Spanish, or Portuguese (customisable via org settings.opsDashboard.guideLanguageExclude).',
    filter: (ctx) => ({
      ...tabContentFilter(OPERATIONS_TAB.Guides),
      languageCodeExclude:
        ctx.guideLanguageExclude && ctx.guideLanguageExclude.length > 0
          ? ctx.guideLanguageExclude
          : [...DEFAULT_GUIDE_LANGUAGES],
    }),
  },
  {
    id: 'guides-long-duration',
    label: 'Long Duration',
    subText: '> 8 hours',
    dot: '#fbbf24',
    description: 'Guide subitems lasting longer than 8 hours.',
    filter: () => ({
      ...tabContentFilter(OPERATIONS_TAB.Guides),
      minDurationHours: 8,
    }),
  },
];

const ticketsCards: OperationsFilterCard[] = [
  {
    id: 'tickets-modified',
    label: 'Modified',
    subText: 'ticket items',
    agingHint: true,
    dot: '#f472b6',
    description: 'Ticket subitems with operation status Modified (S0).',
    filter: () => ({
      ...tabContentFilter(OPERATIONS_TAB.Tickets),
      operationStatusCodes: [CODE_MODIFIED],
    }),
  },
  {
    id: 'tickets-priority-venues',
    label: 'Priority venues',
    subText: 'org-configured',
    dot: '#f97316',
    description:
      'Ticket subitems at venues the organization marked Priority. Set them under System configuration → Ticket Venues; with none marked, the card reads 0.',
    // No venue list travels with the filter (XT-538). The server resolves the
    // org's flagged venues itself, so this stays a boolean through the URL and
    // the client never holds the set. The toolbar Venue filter keeps `venue`.
    filter: () => ({
      ...tabContentFilter(OPERATIONS_TAB.Tickets),
      priorityVenuesOnly: true,
    }),
  },
  {
    id: 'tickets-not-requested',
    label: 'Not Requested',
    subText: 'in window',
    agingHint: true,
    dot: '#fb923c',
    description:
      'Ticket subitems with operation status Non-Requested (S1) in the chosen time window.',
    filter: () => ({
      ...tabContentFilter(OPERATIONS_TAB.Tickets),
      operationStatusCodes: [CODE_NON_REQUESTED],
    }),
  },
  {
    id: 'tickets-no-pdf',
    label: 'No PDF',
    subText: 'missing attachment',
    agingHint: true,
    dot: '#e11d48',
    description:
      'Ticket subitems without a PDF attached, excluding Non-Requested, Delivered/Done (S7), and Cancelled (S90/S91).',
    filter: () => ({
      ...tabContentFilter(OPERATIONS_TAB.Tickets),
      withoutPdf: true,
      // Done is org-titled; in this codebase terminal "done/delivered" is S7.
      operationStatusCodesExclude: [
        CODE_NON_REQUESTED,
        CODE_DELIVERED,
        ...CODE_CANCELLED_LIKE,
      ],
    }),
  },
  {
    id: 'tickets-10-pax',
    label: '+10 Pax',
    subText: 'non-requested',
    agingHint: true,
    dot: '#fbbf24',
    description:
      'Ticket subitems with more than 10 travellers and status Non-Requested (S1).',
    filter: () => ({
      ...tabContentFilter(OPERATIONS_TAB.Tickets),
      minTravellers: 11,
      operationStatusCodes: [CODE_NON_REQUESTED],
    }),
  },
];

const transportCards: OperationsFilterCard[] = [
  {
    id: 'transport-modified',
    label: 'Modified',
    subText: 'transport items',
    agingHint: true,
    dot: '#f472b6',
    description: 'Transport items with operation status Modified (S0).',
    filter: () => ({
      ...tabContentFilter(OPERATIONS_TAB.Transport),
      operationStatusCodes: [CODE_MODIFIED],
    }),
  },
  {
    id: 'transport-non-requested',
    label: 'Non-Requested',
    subText: 'in window',
    agingHint: true,
    dot: '#fb923c',
    description:
      'Transport items with operation status Non-Requested (S1) in the chosen time window.',
    filter: () => ({
      ...tabContentFilter(OPERATIONS_TAB.Transport),
      operationStatusCodes: [CODE_NON_REQUESTED],
    }),
  },
  {
    id: 'transport-no-driver',
    label: 'No Driver',
    subText: 'unassigned',
    agingHint: true,
    dot: '#f97316',
    description:
      'Transport items with no assigned driver, excluding Delivered / Done / Cancelled.',
    filter: () => ({
      ...tabContentFilter(OPERATIONS_TAB.Transport),
      tourOperatorAssigned: 'unassigned',
      operationStatusCodesExclude: [CODE_DELIVERED, ...CODE_CANCELLED_LIKE],
    }),
  },
];

const messagesCards: OperationsFilterCard[] = [
  {
    id: 'messages-modified',
    label: 'Modified',
    subText: 'messages on S0 items',
    agingHint: true,
    dot: '#f472b6',
    description:
      'WhatsApp messages whose linked item has operation status Modified (S0). Click to filter the messages list.',
    filter: () => ({
      operationStatusCodes: [CODE_MODIFIED],
      countMode: 'messages',
    }),
  },
  {
    id: 'messages-expired',
    label: 'No Reply or Expired',
    subText: 'expired messages',
    agingHint: true,
    dot: '#e11d48',
    description:
      'Number of expired WhatsApp messages. Click to filter the messages list.',
    filter: () => ({
      messageStatuses: ['expired'],
      countMode: 'messages',
    }),
  },
  {
    id: 'messages-max-chars',
    label: 'Max characters',
    subText: 'near limit',
    agingHint: true,
    dot: '#fbbf24',
    description:
      'Number of WhatsApp messages at/over the 1024-character limit (send dialog also blocks over-limit). Click to filter the messages list.',
    filter: () => ({
      messageStatuses: ['max_chars'],
      countMode: 'messages',
    }),
  },
  {
    id: 'messages-declined',
    label: 'Declined',
    subText: 'messages',
    dot: '#f97316',
    description:
      'Number of declined WhatsApp messages. Click to filter the messages list.',
    filter: () => ({
      messageStatuses: ['declined'],
      countMode: 'messages',
    }),
  },
  {
    id: 'messages-accepted',
    label: 'Accepted',
    subText: 'messages',
    dot: '#34d399',
    description:
      'Number of accepted WhatsApp messages. Click to filter the messages list.',
    filter: () => ({
      messageStatuses: ['accepted'],
      countMode: 'messages',
    }),
  },
];

/**
 * Maps a Messages-tab metric card id to the external-messages list filter.
 * Keeps the Messages tab on the messages entity (never swaps to items).
 */
export type MessagesTabListFilter = {
  status?: 'expired' | 'accepted' | 'declined';
  maxChars?: boolean;
  operationStatusCodes?: string[];
};

export const messagesTabListFilter = (
  opsCardId?: string
): MessagesTabListFilter => {
  switch (opsCardId) {
    case 'messages-expired':
      return { status: 'expired' };
    case 'messages-accepted':
      return { status: 'accepted' };
    case 'messages-declined':
      return { status: 'declined' };
    case 'messages-max-chars':
      return { maxChars: true };
    case 'messages-modified':
      return { operationStatusCodes: [CODE_MODIFIED] };
    default:
      return {};
  }
};

/** Tab labels for the /operations list (XT-380 card sets). */
export const OPERATIONS_TABS: {
  id: OperationsTab;
  label: string;
}[] = [
  { id: OPERATIONS_TAB.Home, label: 'Home' },
  { id: OPERATIONS_TAB.Guides, label: 'Guides' },
  { id: OPERATIONS_TAB.Tickets, label: 'Tickets' },
  { id: OPERATIONS_TAB.Transport, label: 'Transports' },
  { id: OPERATIONS_TAB.Messages, label: 'Messages' },
];

export const CARDS_BY_TAB: Record<OperationsTab, OperationsFilterCard[]> = {
  [OPERATIONS_TAB.Home]: homeCards,
  [OPERATIONS_TAB.Guides]: guidesCards,
  [OPERATIONS_TAB.Tickets]: ticketsCards,
  [OPERATIONS_TAB.Transport]: transportCards,
  [OPERATIONS_TAB.Messages]: messagesCards,
};

/** Flat list of every XT-380 /operations card (for lookup by id). */
export const OPERATIONS_FILTER_CARDS: OperationsFilterCard[] = [
  ...homeCards,
  ...guidesCards,
  ...ticketsCards,
  ...transportCards,
  ...messagesCards,
];

// No card selected = list shows the tab's category scope only (all items in
// tab). Selecting a card is intentional; the tab itself scopes the list.
export const getFilterCard = (id?: string): OperationsFilterCard | undefined =>
  id ? OPERATIONS_FILTER_CARDS.find((card) => card.id === id) : undefined;

export const getCardsForTab = (tab: OperationsTab): OperationsFilterCard[] =>
  CARDS_BY_TAB[tab] ?? homeCards;
